/* The roster.
 *
 * THE DEFAULT IS CURRENT MEMBERS. A gym's record count is mostly history - at one
 * real tenant, 2,976 records against 565 current - and a roster that opens on
 * everyone who ever joined is a roster nobody can work from. History is asked for.
 *
 * THE DEFAULT SORT IS WORST FIRST. The score counts DOWN from 100, so ascending is
 * the at-risk end. A members table that opens on 100, 100, 100 has buried the only
 * rows anybody needed to see.
 *
 * A FLOOR SEAT OPENS ON ITS OWN BOOK, here as well as on the console, with everyone
 * one click away. A manager and an owner have no book, so there is no switch.
 *
 * SEARCH FINDS PEOPLE, NOT MEMBERS. Somebody typing a name does not know whether that
 * person has signed up yet, and the leads board deliberately shows only the open
 * pipeline, so a won or lost lead is reachable here or nowhere.
 *
 * Filtering, sorting and paging all happen in the SOURCE. The seeded source behaves
 * like a server on purpose; a screen that slices a roster it already holds will not
 * survive contact with 3,000 members.
 */
import { useState } from 'react';
import type { MemberStatus, RiskTier } from '@acrostic/model';
import { notScoredBecause } from '@acrostic/model';
import { useAsync, useSeesCommercials, useSource } from '../data/SourceProvider.js';
import type { MemberQuery } from '../data/contract.js';
import { NoneRow } from '../components/Panel.js';
import { Avatar } from '../components/Avatar.js';
import { tone } from '../components/People.js';
import { fmt } from '../lib/fmt.js';
import { go } from '../routes.js';
import { STAGES } from '../lib/leads.js';
import type { ScreenProps } from '../App.js';

type StatusFilter = MemberStatus | 'current' | 'inactive' | 'all';

const STATUSES: { key: StatusFilter; label: string }[] = [
  { key: 'current', label: 'Current' },
  { key: 'active', label: 'Active' },
  { key: 'suspended', label: 'On hold' },
  { key: 'inactive', label: 'Former' },
  { key: 'all', label: 'Everyone' },
];

const SORTS: { key: string; label: string }[] = [
  { key: 'risk', label: 'Needs attention' },
  { key: 'name', label: 'Name' },
  { key: 'lastVisit', label: 'Last visit' },
  { key: '-joinedAt', label: 'Newest' },
];

const TIERS: RiskTier[] = ['Red', 'Amber', 'Green'];

const SIZE = 25;

export function Members({ user }: ScreenProps) {
  const source = useSource();
  const money = useSeesCommercials();
  const [status, setStatus] = useState<StatusFilter>('current');
  const [sort, setSort] = useState('risk');
  const [tier, setTier] = useState<RiskTier | 'all'>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  /* A support coach's own members; a coach's own. Neither a manager nor an owner has
     a book of their own, so neither gets the switch. */
  const book = user.roles.includes('sales') ? 'supportCoachId'
    : user.roles.includes('coach') ? 'coachId' : null;
  const [mine, setMine] = useState(!!book);

  const query: MemberQuery = {
    status, sort, page, size: SIZE,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(tier === 'all' ? {} : { tier }),
    ...(mine && book === 'supportCoachId' ? { supportCoachId: user.id } : {}),
    ...(mine && book === 'coachId' ? { coachId: user.id } : {}),
  };

  const { data, error } = useAsync(() => source.getMembers(query), [source, status, sort, tier, q, page, mine]);
  /* Leads are searched only when somebody is actually searching: the roster is the
     screen, and the people who are not members yet are the answer to a question. */
  const people = useAsync(
    () => q.trim().length >= 2 ? source.searchPeople(q.trim()) : Promise.resolve(null),
    [source, q],
  );
  const leads = people.data?.leads ?? [];

  const set = <T,>(fn: (v: T) => void) => (v: T) => { fn(v); setPage(1); };

  return (
    <>
      <section className="panel">
        <header>
          <h2 className="t-heading-h3">Members</h2>
          <span className="eyebrow t-label-xsmall">
            {data ? `${fmt.num(data.total)} ${q ? 'matching' : mine ? 'in your book' : status === 'current' ? 'current' : 'matching'}` : ''}
          </span>
          <span className="spacer" />
          <input
            className="field t-body-small"
            placeholder="Search members and leads by name"
            value={q}
            onChange={(e) => set(setQ)(e.target.value)}
          />
        </header>

        <div className="filters">
          {book && (
            <div className="seg" role="group" aria-label="Whose members">
              <button type="button" className={'segbtn t-label-small' + (mine ? ' on' : '')}
                onClick={() => set(setMine)(true)}>Mine</button>
              <button type="button" className={'segbtn t-label-small' + (mine ? '' : ' on')}
                onClick={() => set(setMine)(false)}>Everyone</button>
            </div>
          )}
          <div className="seg" role="group" aria-label="Status">
            {STATUSES.map((s) => (
              <button key={s.key} type="button"
                className={'segbtn t-label-small' + (s.key === status ? ' on' : '')}
                onClick={() => set(setStatus)(s.key)}>{s.label}</button>
            ))}
          </div>
          <span className="spacer" />
          <label className="t-caption muted">
            Tier{' '}
            <select className="t-label-small" value={tier}
              onChange={(e) => set(setTier)(e.target.value as RiskTier | 'all')}>
              <option value="all">All</option>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="t-caption muted">
            Sort{' '}
            <select className="t-label-small" value={sort} onChange={(e) => set(setSort)(e.target.value)}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>

        {/* A tier filter cannot return a suppressed member, because they hold no tier.
            That is rule 1 holding at the query rather than at the screen, and it is
            worth saying out loud when somebody has filtered them out. */}
        {tier !== 'all' && (
          <p className="t-caption muted">
            Members on Care Track or Hold hold no tier, so they are not in this list.
          </p>
        )}

        {error && <NoneRow>Could not read the roster. {error.message}</NoneRow>}
        {!error && !data && <NoneRow>Reading the roster…</NoneRow>}
        {data && data.items.length === 0 && (
          <NoneRow>{q ? `No member matches “${q}”.` : 'Nobody here.'}</NoneRow>
        )}

        {data && data.items.length > 0 && (
          <table className="table">
            <thead>
              <tr className="t-label-small">
                <th>Name</th><th>Status</th>{money && <th>Plan</th>}
                <th>Last visit</th><th>Joined</th>
                <th className="right">Score</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => {
                /* The one model, or an honest reason there is no number. Never a
                   stale one, and never a zero standing in for "we do not know". */
                const off = notScoredBecause(m);
                const score = off ? null : m.risk?.score ?? null;
                return (
                  <tr key={m.id} onClick={() => go('/members/' + m.id)} tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') go('/members/' + m.id); }}>
                    <td>
                      {score == null
                        ? <span className="dot" style={{ background: 'transparent' }} aria-hidden="true" />
                        : <span className={'dot ' + tone(score)} aria-hidden="true" />}
                      <Avatar name={m.name} photoUrl={m.photoUrl} size="sm" />
                      <span className="t-label-large">{m.name}</span>
                    </td>
                    <td className="t-body-small muted">
                      {m.suppression ? (m.suppression.kind === 'hold' ? 'On hold' : 'Care Track') : m.status}
                    </td>
                    {money && <td className="t-body-small muted">{m.plan ?? '—'}</td>}
                    <td className="t-body-small muted">{fmt.ago(m.lastVisit)}</td>
                    <td className="t-body-small muted">{fmt.date(m.joinedAt)}</td>
                    <td className="right t-label-medium">
                      {score == null
                        ? <span className="pill flat t-caption" title={off ?? ''}>{off ?? 'Not scored'}</span>
                        : score}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {data && data.total > SIZE && (
          <div className="pager t-label-small">
            <button type="button" className="btn" disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span className="muted">
              {(page - 1) * SIZE + 1}–{Math.min(page * SIZE, data.total)} of {fmt.num(data.total)}
            </span>
            <button type="button" className="btn" disabled={page * SIZE >= data.total}
              onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </section>

      {/* ONE PERSON, ONE JOURNEY. A search that found somebody who has not joined yet
          has found the person being looked for, whatever half of the product they
          are on. Won and lost included: the board shows neither. */}
      {q.trim().length >= 2 && leads.length > 0 && (
        <section className="panel">
          <header>
            <h2 className="t-heading-h3">Leads</h2>
            <span className="eyebrow t-label-xsmall">
              {leads.length} {leads.length === 1 ? 'person' : 'people'} who have not joined
            </span>
          </header>
          <div className="rows">
            {leads.map((l) => (
              <button key={l.id} type="button" className="row clickable"
                onClick={() => go('/leads/' + l.id)}>
                <Avatar name={l.name} photoUrl={l.photoUrl} size="sm" />
                <div className="who">
                  <div className="n t-label-large">{l.name}</div>
                  <div className="s t-caption muted">
                    {[l.source, l.owner ? 'routed to ' + l.owner : null].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span className={'badge ' + STAGES[l.stage].tone + ' t-label-xsmall'}>
                  {STAGES[l.stage].long}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
